import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import './UniversalProductLibrary.css';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

// SVG Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

export default function UniversalProductBulkUpload({ onClose, onUploadComplete, categories, countries }) {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();

  const [jsonInput, setJsonInput] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [parsedProducts, setParsedProducts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const exampleJson = JSON.stringify({
    products: [
      {
        name: "Organic Valley Whole Milk",
        brand: "Organic Valley",
        category: "Dairy & Eggs",
        subcategory: "Milk",
        country: "US",
        upc: "093966001006",
        size: "64 oz",
        sizeValue: 64,
        sizeUnit: "oz",
        msrp: 6.99,
        tags: ["milk", "organic", "whole milk"],
        keywords: ["organic valley", "organic milk"],
        images: [
          {
            url: "https://example.com/organic-valley-milk.jpg",
            isPrimary: true,
            source: "manufacturer"
          }
        ]
      },
      {
        name: "Great Value 2% Milk",
        brand: "Great Value",
        category: "Dairy & Eggs",
        subcategory: "Milk",
        country: "CA",
        upc: "068113192327",
        size: "4 L",
        sizeValue: 4,
        sizeUnit: "L",
        msrp: 5.97,
        tags: ["milk", "2%", "dairy"],
        keywords: ["great value", "walmart", "milk"]
      }
    ]
  }, null, 2);

  const validateProducts = () => {
    setValidationErrors([]);
    setValidationSuccess(false);
    setParsedProducts([]);

    try {
      const data = JSON.parse(jsonInput);

      if (!data.products || !Array.isArray(data.products)) {
        setValidationErrors(['JSON must contain a "products" array']);
        return;
      }

      if (data.products.length === 0) {
        setValidationErrors(['Products array cannot be empty']);
        return;
      }

      if (data.products.length > 500) {
        setValidationErrors(['Maximum 500 products allowed per upload']);
        return;
      }

      const errors = [];
      const validProducts = [];

      data.products.forEach((product, index) => {
        const productErrors = [];

        if (!product.name || typeof product.name !== 'string') {
          productErrors.push(`Product ${index + 1}: "name" is required and must be a string`);
        }

        if (!product.category || typeof product.category !== 'string') {
          productErrors.push(`Product ${index + 1}: "category" is required and must be a string`);
        } else if (!categories.includes(product.category)) {
          productErrors.push(`Product ${index + 1}: Invalid category "${product.category}"`);
        }

        if (!product.country || typeof product.country !== 'string') {
          productErrors.push(`Product ${index + 1}: "country" is required and must be a string`);
        } else if (!countries.find(c => c.code === product.country)) {
          productErrors.push(`Product ${index + 1}: Invalid country code "${product.country}"`);
        }

        if (product.msrp !== undefined && typeof product.msrp !== 'number') {
          productErrors.push(`Product ${index + 1}: "msrp" must be a number`);
        }

        if (product.sizeValue !== undefined && typeof product.sizeValue !== 'number') {
          productErrors.push(`Product ${index + 1}: "sizeValue" must be a number`);
        }

        if (product.packSize !== undefined && typeof product.packSize !== 'number') {
          productErrors.push(`Product ${index + 1}: "packSize" must be a number`);
        }

        if (product.tags !== undefined && !Array.isArray(product.tags)) {
          productErrors.push(`Product ${index + 1}: "tags" must be an array`);
        }

        if (product.aliases !== undefined && !Array.isArray(product.aliases)) {
          productErrors.push(`Product ${index + 1}: "aliases" must be an array`);
        }

        if (product.keywords !== undefined && !Array.isArray(product.keywords)) {
          productErrors.push(`Product ${index + 1}: "keywords" must be an array`);
        }

        if (product.images !== undefined) {
          if (!Array.isArray(product.images)) {
            productErrors.push(`Product ${index + 1}: "images" must be an array`);
          } else {
            product.images.forEach((img, imgIndex) => {
              if (!img.url || typeof img.url !== 'string') {
                productErrors.push(`Product ${index + 1}, Image ${imgIndex + 1}: "url" is required`);
              }
            });
          }
        }

        if (productErrors.length === 0) {
          validProducts.push(product);
        } else {
          errors.push(...productErrors);
        }
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
      } else {
        setValidationSuccess(true);
        setParsedProducts(validProducts);
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setValidationErrors(['Invalid JSON format. Please check your syntax.']);
      } else {
        setValidationErrors(['An error occurred while parsing the JSON']);
      }
    }
  };

  const handleUpload = async () => {
    if (parsedProducts.length === 0) return;

    setIsUploading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${API_BASE}/api/universal-products/admin/bulk-upload`,
        { products: parsedProducts },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { created, errors: uploadErrors } = response.data;
      if (uploadErrors > 0) {
        notification.warning(`Uploaded ${created} products with ${uploadErrors} errors`);
      } else {
        notification.success(`Successfully uploaded ${created} products`);
      }
      onUploadComplete();
    } catch (error) {
      notification.error(error.response?.data?.message || 'Failed to upload products');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([exampleJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="upl-subpage-header">
        <div className="upl-subpage-header-left">
          <button className="upl-back-btn" onClick={onClose}>
            <ArrowLeftIcon />
          </button>
          <div>
            <h1 className="upl-subpage-title">Bulk Upload Products</h1>
            <p className="upl-subpage-subtitle">Upload multiple products at once using JSON format</p>
          </div>
        </div>
        <div className="upl-subpage-actions">
          <button className="upl-btn outline-gray" onClick={onClose}>Cancel</button>
          <button
            className="upl-btn primary"
            onClick={handleUpload}
            disabled={!validationSuccess || parsedProducts.length === 0 || isUploading}
          >
            <UploadIcon />
            {isUploading ? 'Uploading...' : `Upload${parsedProducts.length > 0 ? ` (${parsedProducts.length})` : ''}`}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="upl-scroll-content">
        <div className="upl-bulk-content">
          {/* Instructions */}
          <div className="upl-instructions">
            <h3>Upload Instructions</h3>
            <ul>
              <li>Maximum 500 products per upload</li>
              <li>Required fields: name, category, country</li>
              <li>Use ISO 3166-1 alpha-2 country codes (CA, US)</li>
              <li>Download the template below for reference</li>
            </ul>
          </div>

          {/* Template Download */}
          <div className="upl-template-section">
            <div className="upl-template-header">
              <div className="upl-template-header-left">
                <FileTextIcon />
                <div>
                  <h3 className="upl-template-title">Template File</h3>
                  <p className="upl-template-subtitle">Download an example JSON file to get started</p>
                </div>
              </div>
              <button className="upl-btn outline" onClick={downloadTemplate}>
                <DownloadIcon />
                Download Template
              </button>
            </div>
            <div className="upl-template-preview">
              <pre>{exampleJson}</pre>
            </div>
          </div>

          {/* JSON Input */}
          <div className="upl-json-section">
            <div className="upl-json-header">
              <h3 className="upl-json-title">Paste Your JSON</h3>
              <button className="upl-btn outline" onClick={validateProducts}>
                Validate JSON
              </button>
            </div>
            <textarea
              className="upl-json-textarea"
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setValidationErrors([]);
                setValidationSuccess(false);
                setParsedProducts([]);
              }}
              placeholder="Paste your JSON here..."
            />
          </div>

          {/* Validation Results */}
          {validationSuccess && (
            <div className="upl-validation-success">
              <CheckCircleIcon />
              <div>
                <h3>Validation Successful!</h3>
                <p>{parsedProducts.length} product{parsedProducts.length !== 1 ? 's' : ''} ready to upload.</p>
              </div>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="upl-validation-errors">
              <AlertCircleIcon />
              <div>
                <h3>Validation Errors</h3>
                <ul>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Valid Categories Reference */}
          <div className="upl-reference-card">
            <h3 className="upl-reference-title">Valid Categories</h3>
            <div className="upl-pills">
              {categories.map((category) => (
                <span key={category} className="upl-pill purple">{category}</span>
              ))}
            </div>
          </div>

          {/* Valid Countries Reference */}
          <div className="upl-reference-card">
            <h3 className="upl-reference-title">Valid Country Codes</h3>
            <div className="upl-pills">
              {countries.map((country) => (
                <span key={country.code} className="upl-pill blue">{country.code} - {country.name}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
